/**
 * Contact CSV Processor
 * Main orchestrator for contact CSV upload processing pipeline
 *
 * Flow:
 * 1. Parse CSV file
 * 2. Transform rows using PMS-specific contact transformer
 * 3. Detect duplicates within batch and against database
 * 4. Return preview data for user confirmation
 */

import Papa from 'papaparse'
import type {
  CSVRow,
  ContactUploadContext,
  ContactTransformer,
} from '@/types/csv-upload'
import type { ContactPropertyRelationshipRecord } from '@super/shared-types'
import { IntegrationSource } from '@super/shared-types'
import { appfolioTransformer } from './transformers/appfolio-transformer'
import { DuplicateDetector } from './duplicate-detector'
import type { DuplicateDetectionResult } from './duplicate-detector'
import type { ContactWithRelationships } from '@super/shared-types'
import { ContactResolutionEngine } from './contact-resolution-engine'
import type { ContactResolutionPlan } from '@super/shared-types'
import { MemoryMonitor } from './utils/memory-monitor'

export interface CSVProcessingOptions {
  skipEmptyLines?: boolean
  trimHeaders?: boolean
  trimValues?: boolean
  skipRows?: number
  maxMemoryMB?: number // Memory limit in megabytes
}

export interface CSVProcessingResult {
  success: boolean
  totalRows: number
  processedRows: number
  skipped: number
  contacts: ContactPropertyRelationshipRecord[]
  duplicateDetection?: DuplicateDetectionResult
  resolutionPlan?: ContactResolutionPlan
  errors: Array<{
    row: number
    message: string
    data?: any
  }>
  warnings: Array<{
    row: number
    message: string
    data?: any
  }>
  memoryUsage?: {
    used: number
    limit: number
    percentage: number
  }
}

export interface CSVPreviewResult extends CSVProcessingResult {
  sampleRows: CSVRow[]
  headers: string[]
}

/**
 * Get the appropriate transformer for a PMS provider
 */
function getTransformer(provider: IntegrationSource): ContactTransformer {
  switch (provider) {
    case IntegrationSource.APPFOLIO:
      return appfolioTransformer
    // TODO: Add other transformers
    // case IntegrationSource.BUILDIUM:
    //   return buildiumTransformer
    default:
      throw new Error(`Unsupported PMS provider: ${provider}`)
  }
}

/**
 * Contact CSV processor class
 */
export class ContactCSVProcessor {
  private transformer: ContactTransformer
  private context: ContactUploadContext
  private options: CSVProcessingOptions
  private memoryMonitor: MemoryMonitor

  constructor(
    context: ContactUploadContext,
    options: CSVProcessingOptions = {},
  ) {
    this.context = context
    this.transformer = getTransformer(context.pmsProvider)
    this.options = {
      skipEmptyLines: true,
      trimHeaders: true,
      trimValues: true,
      skipRows: 0,
      maxMemoryMB: 200,
      ...options,
    }
    this.memoryMonitor = new MemoryMonitor({
      limitMB: this.options.maxMemoryMB,
    })
  }

  /**
   * Parse CSV file and return preview data
   */
  async parseCSV(file: File): Promise<CSVPreviewResult> {
    // Check file size before processing
    const fileSizeStatus = this.memoryMonitor.validateFileSize(file)
    if (!fileSizeStatus.safe) {
      return {
        success: false,
        totalRows: 0,
        processedRows: 0,
        skipped: 0,
        contacts: [],
        errors: [
          {
            row: 0,
            message: fileSizeStatus.message!,
          },
        ],
        warnings: [],
        sampleRows: [],
        headers: [],
        memoryUsage: {
          used: fileSizeStatus.used,
          limit: fileSizeStatus.limit,
          percentage: fileSizeStatus.percentage,
        },
      }
    }

    return new Promise((resolve, reject) => {
      Papa.parse<CSVRow>(file, {
        header: true,
        skipEmptyLines: this.options.skipEmptyLines,
        transformHeader: header => {
          return this.options.trimHeaders ? header.trim() : header
        },
        transform: value => {
          return this.options.trimValues ? value.trim() : value
        },
        complete: results => {
          const preview = this.processCSVResults(results)
          resolve(preview)
        },
        error: error => {
          reject(new Error(`CSV parsing failed: ${error.message}`))
        },
      })
    })
  }

  /**
   * Parse CSV text directly
   */
  parseCSVText(csvText: string): CSVPreviewResult {
    // Check memory before parsing
    const memoryStatus = this.memoryMonitor.checkMemory()
    if (!memoryStatus.safe) {
      return {
        success: false,
        totalRows: 0,
        processedRows: 0,
        skipped: 0,
        contacts: [],
        errors: [
          {
            row: 0,
            message: memoryStatus.message || 'Memory limit exceeded',
          },
        ],
        warnings: [],
        sampleRows: [],
        headers: [],
        memoryUsage: {
          used: memoryStatus.used,
          limit: memoryStatus.limit,
          percentage: memoryStatus.percentage,
        },
      }
    }

    const results = Papa.parse<CSVRow>(csvText, {
      header: true,
      skipEmptyLines: this.options.skipEmptyLines,
      transformHeader: header => {
        return this.options.trimHeaders ? header.trim() : header
      },
      transform: value => {
        return this.options.trimValues ? value.trim() : value
      },
    })

    return this.processCSVResults(results)
  }

  /**
   * Process parsed CSV results
   */
  private processCSVResults(
    results: Papa.ParseResult<CSVRow>,
  ): CSVPreviewResult {
    const errors: CSVPreviewResult['errors'] = []
    const warnings: CSVPreviewResult['warnings'] = []
    const contacts: ContactPropertyRelationshipRecord[] = []
    let skipped = 0

    // Skip header rows if configured
    const dataRows = results.data.slice(this.options.skipRows || 0)

    // Process each row
    dataRows.forEach((row, index) => {
      const rowNumber = index + 1 + (this.options.skipRows || 0)

      // Check memory periodically (every 100 rows)
      if (index % 100 === 0) {
        const memoryStatus = this.memoryMonitor.checkMemory()
        if (!memoryStatus.safe) {
          errors.push({
            row: rowNumber,
            message:
              memoryStatus.message || 'Memory limit exceeded during processing',
          })
          return // Stop processing
        }
      }

      try {
        // Transform the row
        const contactResult = this.transformer.transformContact(
          row,
          this.context,
        )

        if (contactResult) {
          // Handle both single record and array of records (for owners with multiple properties)
          const contactsToProcess = Array.isArray(contactResult)
            ? contactResult
            : [contactResult]

          // Validate and add each contact
          contactsToProcess.forEach(contact => {
            const validation = this.transformer.validateContact(
              contact,
              this.context,
            )

            if (validation.isValid) {
              contacts.push(contact)

              // Add any warnings
              validation.warnings?.forEach(warning => {
                warnings.push({
                  row: rowNumber,
                  message: warning,
                  data: row,
                })
              })
            } else {
              // Convert validation errors to warnings - skip invalid rows but don't block upload
              skipped++
              validation.errors.forEach(error => {
                warnings.push({
                  row: rowNumber,
                  message: `Row skipped: ${error}`,
                  data: row,
                })
              })

              // Add any validation warnings as well
              validation.warnings?.forEach(warning => {
                warnings.push({
                  row: rowNumber,
                  message: warning,
                  data: row,
                })
              })
            }
          })
        } else {
          skipped++
          warnings.push({
            row: rowNumber,
            message:
              'Row skipped: Unable to transform row data into valid contact (missing required fields)',
            data: row,
          })
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message:
            error instanceof Error
              ? error.message
              : 'Unknown error during transformation',
          data: row,
        })
      }
    })

    // Add parsing errors if any
    results.errors.forEach(error => {
      errors.push({
        row: error.row !== undefined ? error.row + 1 : 0,
        message: error.message,
        data: error,
      })
    })

    // Get final memory usage
    const finalMemoryStatus = this.memoryMonitor.checkMemory()

    return {
      success: errors.length === 0, // Only critical errors (parsing, memory, transformation exceptions) block upload
      totalRows: results.data.length,
      processedRows: contacts.length,
      skipped,
      contacts,
      errors,
      warnings,
      sampleRows: results.data.slice(0, 5), // First 5 rows for preview
      headers: results.meta.fields || [],
      memoryUsage: {
        used: finalMemoryStatus.used,
        limit: finalMemoryStatus.limit,
        percentage: finalMemoryStatus.percentage,
      },
    }
  }

  /**
   * Detect duplicates in the processed contacts
   */
  async detectDuplicates(
    contacts: ContactPropertyRelationshipRecord[],
    existingContacts: ContactWithRelationships[],
  ): Promise<DuplicateDetectionResult> {
    // Check memory before duplicate detection
    const memoryStatus = this.memoryMonitor.checkMemory()
    if (!memoryStatus.safe) {
      throw new Error(
        memoryStatus.message ||
          'Memory limit exceeded before duplicate detection',
      )
    }

    const detector = new DuplicateDetector(existingContacts)
    return detector.detectDuplicates(contacts)
  }

  /**
   * Full processing pipeline: parse, transform, and detect duplicates
   */
  async processFile(
    file: File,
    existingContacts: ContactWithRelationships[] = [],
  ): Promise<CSVProcessingResult> {
    // Step 1: Parse and transform
    const preview = await this.parseCSV(file)

    if (!preview.success) {
      return preview
    }

    // Step 2: Detect duplicates if we have valid contacts
    if (preview.contacts.length > 0) {
      try {
        const duplicateDetection = await this.detectDuplicates(
          preview.contacts,
          existingContacts,
        )

        // Step 3: Generate resolution plan from unified groups and unique contacts
        const resolutionEngine = new ContactResolutionEngine(this.context)
        const resolutionPlan = resolutionEngine.resolveGroups(
          duplicateDetection.groups,
          duplicateDetection.uniqueContacts,
        )

        return {
          ...preview,
          duplicateDetection,
          resolutionPlan,
        }
      } catch (error) {
        preview.errors.push({
          row: 0,
          message:
            error instanceof Error
              ? error.message
              : 'Processing pipeline failed',
        })
        return {
          ...preview,
          success: false,
        }
      }
    }

    return preview
  }

  /**
   * Get statistics about the processed data
   */
  getStatistics(result: CSVProcessingResult): {
    totalRows: number
    validContacts: number
    updateGroups: number
    createGroups: number
    uniqueContacts: number
    relationshipActions: number
    errors: number
    warnings: number
    memoryUsageMB: number
  } {
    const duplicateDetection = result.duplicateDetection
    const resolutionPlan = result.resolutionPlan

    return {
      totalRows: result.totalRows,
      validContacts: result.contacts.length,
      updateGroups:
        resolutionPlan?.summary.updatesCount ||
        duplicateDetection?.groups.filter(g => g.operation === 'update')
          .length ||
        0,
      createGroups:
        resolutionPlan?.summary.duplicateCreatesCount ||
        duplicateDetection?.groups.filter(g => g.operation === 'create')
          .length ||
        0,
      uniqueContacts: duplicateDetection?.uniqueContacts.length || 0,
      relationshipActions:
        resolutionPlan?.summary.relationshipUpdatesCount || 0,
      errors: result.errors.length,
      warnings: result.warnings.length,
      memoryUsageMB: this.memoryMonitor.getUsageMB(),
    }
  }
}
